import React, { useState, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Plus, Trash2, Tag, Link2, Unlink } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { API_URL, getToken } from './constants';

const CategoryManager = ({ entityId, entityName, defaultCategories, customCategories, onCategoriesChange, badgeColor }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newConnected, setNewConnected] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [_editId, _setEditId] = useState(null);

  const getAuthHeaders = () => ({ 'Authorization': `Bearer ${getToken()}` });

  const allCategories = useMemo(() => {
    const customs = (customCategories || []).map(c => ({ ...c, is_custom: true }));
    return [...defaultCategories.map(c => ({ ...c, is_custom: false })), ...customs];
  }, [defaultCategories, customCategories]);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('label', newLabel.trim());
      formData.append('connected', newConnected);
      formData.append('description', newDescription);
      const res = await fetch(`${API_URL}/api/ged/categories/${entityId}`, {
        method: 'POST', headers: getAuthHeaders(), body: formData
      });
      if (res.ok) {
        setNewLabel(''); setNewConnected(false); setNewDescription(''); setShowAdd(false);
        onCategoriesChange();
      }
    } catch { }
    finally { setSaving(false); }
  };

  const toggleConnection = async (cat) => {
    if (!cat.is_custom) return;
    try {
      const formData = new FormData();
      formData.append('connected', !cat.connected);
      await fetch(`${API_URL}/api/ged/categories/${entityId}/${cat.id}`, {
        method: 'PUT', headers: getAuthHeaders(), body: formData
      });
      onCategoriesChange();
    } catch { }
  };

  const handleDelete = async (catId) => {
    if (!window.confirm('Supprimer cette categorie personnalisee ?')) return;
    try {
      await fetch(`${API_URL}/api/ged/categories/${entityId}/${catId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      onCategoriesChange();
    } catch { }
  };

  return (
    <div className="border rounded-lg bg-white" data-testid="category-manager">
      <div className="flex items-center justify-between p-3 border-b bg-gray-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-800">Categories – {entityName}</span>
          <Badge className={cn("text-[10px] text-white", badgeColor)}>{allCategories.length}</Badge>
        </div>
        <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 gap-1" onClick={() => setShowAdd(!showAdd)} data-testid="add-category-btn">
          <Plus className="w-3 h-3" /> Ajouter
        </Button>
      </div>

      {showAdd && (
        <div className="p-3 border-b bg-amber-50/50 space-y-2" data-testid="add-category-form">
          <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Nom de la categorie" className="h-8 text-sm" data-testid="new-category-label" />
          <Input value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="Description (optionnel)" className="h-8 text-sm" />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer text-xs">
              <input type="checkbox" checked={newConnected} onChange={(e) => setNewConnected(e.target.checked)} className="rounded border-gray-300" data-testid="new-category-connected" />
              <Link2 className="w-3 h-3 text-blue-600" />
              Connecter aux documents (identification nom, prenom, espace)
            </label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowAdd(false)}>Annuler</Button>
            <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={handleAdd} disabled={saving || !newLabel.trim()} data-testid="save-category-btn">
              {saving ? '...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      )}

      <div className="divide-y max-h-64 overflow-y-auto">
        {allCategories.map((cat, i) => (
          <div key={cat.value || i} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50/50 group" data-testid={`category-item-${cat.value}`}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className={cn("w-1.5 h-6 rounded-full flex-shrink-0", cat.is_custom ? "bg-amber-500" : "bg-gray-300")} />
              <div className="min-w-0">
                <span className="text-sm text-gray-800">{cat.label}</span>
                {cat.description && <p className="text-[10px] text-gray-500 truncate">{cat.description}</p>}
              </div>
              {cat.is_custom && <Badge className="text-[10px] bg-amber-600 text-white ml-1 font-semibold">personnalisee</Badge>}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => cat.is_custom && toggleConnection(cat)}
                className={cn("p-1 rounded transition-colors", cat.connected ? "text-blue-600 bg-blue-50" : "text-gray-400", cat.is_custom && "hover:bg-blue-100 cursor-pointer")}
                title={cat.connected ? "Connecte (identification active)" : "Non connecte"}
                data-testid={`toggle-connect-${cat.value}`}
              >
                {cat.connected ? <Link2 className="w-3.5 h-3.5" /> : <Unlink className="w-3.5 h-3.5" />}
              </button>
              {cat.is_custom && (
                <button onClick={() => handleDelete(cat.id)} className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-category-${cat.value}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryManager;
